import type {TreeNodeData} from '@mantine/core';
import {useTree} from '@mantine/core';
import {useDebouncedValue} from '@mantine/hooks';
import {useEffect, useMemo, useState} from 'react';
import {useGetAssetTreeQuery} from 'src/redux/queries/asset';
import {filterTreeData} from 'src/shared/utils/treeUtils';

export interface UseAssetTreeContentOptions {
  treeQueryParams?: {
    parentId?: number;
    isLeaf?: boolean;
    activeStates?: boolean[];
  };
  skipQuery?: boolean;
  excludeNodeId?: number;
  initialSelectedId?: string;
  expandOnMount?: boolean;
}

export interface UseAssetTreeContentReturn {
  searchTerm: string;
  handleSearchTerm: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  resetSearch: () => void;
  isLoading: boolean;
  filteredTreeNodeData: TreeNodeData[];
  tree: ReturnType<typeof useTree>;
  refetch: () => void | Promise<unknown>;
}

export const useAssetTreeContent = (
  options: UseAssetTreeContentOptions = {}
): UseAssetTreeContentReturn => {
  const {
    treeQueryParams = {
      parentId: undefined,
      isLeaf: undefined,
      activeStates: [true],
    },
    skipQuery = false,
    excludeNodeId,
    initialSelectedId,
    expandOnMount = true,
  } = options;

  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearchTerm] = useDebouncedValue(searchTerm, 100);

  const {
    data,
    isLoading: isDataLoading,
    isFetching: isDataFetching,
    refetch,
  } = useGetAssetTreeQuery(
    {
      parentId: treeQueryParams.parentId ?? undefined,
      isLeaf: treeQueryParams.isLeaf ?? undefined,
      activeStates: treeQueryParams.activeStates ?? [],
    },
    {
      skip: skipQuery,
    }
  );

  const isLoading = isDataLoading || isDataFetching;

  const treeNodeData = useMemo(() => {
    return (data ?? []) as TreeNodeData[];
  }, [data]);

  const filteredTreeNodeData = useMemo(() => {
    const filterFn = excludeNodeId
      ? (node: TreeNodeData) => node.value !== excludeNodeId.toString()
      : undefined;

    return filterTreeData(treeNodeData, debouncedSearchTerm, filterFn);
  }, [debouncedSearchTerm, treeNodeData, excludeNodeId]);

  const tree = useTree({
    multiple: false,
  });

  useEffect(() => {
    if (initialSelectedId) {
      tree.select(initialSelectedId);
    } else {
      tree.clearSelected();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSelectedId]);

  useEffect(() => {
    if (expandOnMount && filteredTreeNodeData.length > 0) {
      tree.expandAllNodes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandOnMount, filteredTreeNodeData]);

  const handleSearchTerm = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const {value} = e.target;
    setSearchTerm(value);
  };

  const resetSearch = () => {
    setSearchTerm('');
  };

  return {
    searchTerm,
    handleSearchTerm,
    resetSearch,
    isLoading,
    filteredTreeNodeData,
    tree,
    refetch,
  };
};
